import { useCallback, useMemo, useState } from 'react';
import type { DragEndEvent } from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';
import { useTranslation } from 'react-i18next';
import type {
  PartResponseDto,
  TriggerResponseDto,
  WorkTypeResponseDto,
} from '@evimo/client/models';
import { useCreateTask, useTasks } from '~/app/hooks/maintenance/useTasks';
import {
  useCreateTrigger,
  useTriggers,
} from '~/app/hooks/maintenance/useTriggers';
import { useParts } from '~/app/hooks/maintenance/useParts';
import { useWorkTypes } from '~/app/hooks/maintenance/useWorkTypes';
import { formatTriggerSelectLabel } from '~/app/components/maintenance/tasks-triggers/utils';
import type {
  WizardData,
  WizardTask,
} from '~/app/components/maintenance/preventive/wizard/types';
import {
  takeLatestTaskSuggestions,
  toInlineCreateTaskRequest,
  wizardTasksHaveTriggers,
} from '~/app/components/maintenance/preventive/wizard/StepTasks/utils';
import type {
  InlineTaskFormValues,
  InlineTriggerFormValues,
} from '~/app/components/maintenance/preventive/wizard/StepTasks/types';
import type { TaskResponseDto } from '@evimo/client/models';

type UseStepTasksParams = {
  data: WizardData;
  onChange: (patch: Partial<WizardData>) => void;
};

export function useStepTasks({ data, onChange }: UseStepTasksParams) {
  const { t } = useTranslation('maintenance');
  const { data: libraryTasks = [] } = useTasks();
  const { data: triggers = [] } = useTriggers();
  const { data: workTypes = [] } = useWorkTypes();
  const { parts } = useParts();
  const createTaskMutation = useCreateTask();
  const createTriggerMutation = useCreateTrigger();

  const [inlineTaskOpen, setInlineTaskOpen] = useState(false);
  const [inlineTriggerForTaskId, setInlineTriggerForTaskId] = useState<
    string | null
  >(null);

  const triggerMap = useMemo(
    () =>
      new Map(
        (triggers as TriggerResponseDto[]).map((tr) => [
          tr.id,
          formatTriggerSelectLabel(tr.basis, tr.conditions),
        ]),
      ),
    [triggers],
  );

  const workTypeMap = useMemo(
    () =>
      new Map(
        (workTypes as WorkTypeResponseDto[]).map((wt) => [wt.id, wt.code]),
      ),
    [workTypes],
  );

  const addedTaskIds = useMemo(
    () => new Set(data.tasks.map((task) => task.taskId)),
    [data.tasks],
  );

  const suggestions = useMemo(() => {
    const available = (libraryTasks as TaskResponseDto[]).filter(
      (task) => !addedTaskIds.has(task.id),
    );
    return takeLatestTaskSuggestions(available).map((task) => {
      const code = workTypeMap.get(task.workTypeId) ?? '';
      return {
        taskId: task.id,
        name: task.name,
        workTypeLabel: code ? t(`workType.${code}`) : '',
        mandatory: task.mandatory,
      };
    });
  }, [libraryTasks, addedTaskIds, workTypeMap, t]);

  const triggerOptions = useMemo(
    () =>
      (triggers as TriggerResponseDto[]).map((tr) => ({
        label: formatTriggerSelectLabel(tr.basis, tr.conditions),
        value: tr.id,
      })),
    [triggers],
  );

  const workTypeOptions = useMemo(
    () =>
      (workTypes as WorkTypeResponseDto[]).map((wt) => ({
        label: t(`workType.${wt.code}`),
        value: wt.id,
      })),
    [workTypes, t],
  );

  const partOptions = useMemo(
    () =>
      (parts as PartResponseDto[]).map((part) => ({
        label: part.name,
        value: part.id,
      })),
    [parts],
  );

  const toWizardTask = useCallback(
    (task: TaskResponseDto, triggerId = '', triggerLabel = ''): WizardTask => {
      const code = workTypeMap.get(task.workTypeId) ?? '';
      return {
        taskId: task.id,
        name: task.name,
        triggerId,
        triggerLabel,
        workTypeLabel: code ? t(`workType.${code}`) : '',
        mandatory: task.mandatory,
        subtaskNames: (task.subtasks ?? []).map((subtask) => subtask.name),
      };
    },
    [workTypeMap, t],
  );

  const handleAddSuggestion = useCallback(
    (taskId: string) => {
      const task = (libraryTasks as TaskResponseDto[]).find(
        (t) => t.id === taskId,
      );
      if (!task || addedTaskIds.has(taskId)) return;
      onChange({ tasks: [...data.tasks, toWizardTask(task)] });
    },
    [libraryTasks, addedTaskIds, data.tasks, onChange, toWizardTask],
  );

  const handleCreateTaskInline = useCallback(
    async (values: InlineTaskFormValues) => {
      const created = await createTaskMutation.mutateAsync(
        toInlineCreateTaskRequest(values),
      );
      onChange({ tasks: [...data.tasks, toWizardTask(created)] });
      setInlineTaskOpen(false);
    },
    [createTaskMutation, data.tasks, onChange, toWizardTask],
  );

  const handleRemove = useCallback(
    (taskId: string) => {
      onChange({ tasks: data.tasks.filter((task) => task.taskId !== taskId) });
    },
    [data.tasks, onChange],
  );

  const handleTriggerChange = useCallback(
    (taskId: string, triggerId: string) => {
      onChange({
        tasks: data.tasks.map((task) =>
          task.taskId === taskId
            ? {
                ...task,
                triggerId,
                triggerLabel: triggerMap.get(triggerId) ?? triggerId,
              }
            : task,
        ),
      });
    },
    [data.tasks, onChange, triggerMap],
  );

  const handleCreateTriggerInline = useCallback(
    async (taskId: string, values: InlineTriggerFormValues) => {
      const created = await createTriggerMutation.mutateAsync({
        basis: values.basis,
        conditions: values.conditions,
      });
      const label = formatTriggerSelectLabel(created.basis, created.conditions);
      onChange({
        tasks: data.tasks.map((task) =>
          task.taskId === taskId
            ? { ...task, triggerId: created.id, triggerLabel: label }
            : task,
        ),
      });
      setInlineTriggerForTaskId(null);
    },
    [createTriggerMutation, data.tasks, onChange],
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;
      const oldIndex = data.tasks.findIndex(
        (task) => task.taskId === active.id,
      );
      const newIndex = data.tasks.findIndex((task) => task.taskId === over.id);
      onChange({ tasks: arrayMove(data.tasks, oldIndex, newIndex) });
    },
    [data.tasks, onChange],
  );

  return {
    suggestions,
    triggerOptions,
    workTypeOptions,
    partOptions,
    inlineTaskOpen,
    setInlineTaskOpen,
    inlineTriggerForTaskId,
    setInlineTriggerForTaskId,
    handleAddSuggestion,
    handleCreateTaskInline,
    handleRemove,
    handleTriggerChange,
    handleCreateTriggerInline,
    handleDragEnd,
    isCreatingTask: createTaskMutation.isPending,
    isCreatingTrigger: createTriggerMutation.isPending,
    canContinue: wizardTasksHaveTriggers(data.tasks),
  };
}
